import { Injectable } from '@angular/core';
import { filterNilValue, HashMap, QueryEntity } from '@datorama/akita';
import { PrivateAssetState, PrivateAssetStore } from './private-asset.store';
import {
    filter,
    flatMap,
    map,
    pluck,
    shareReplay,
    switchMap,
    take
} from 'rxjs/operators';
import { FormatCurrencyPipe } from '../../../../shared/pipes/format-currency.pipe';
import { kpiInitialState, PrivateAsset } from './private-asset.model';
import { combineLatest, Observable } from 'rxjs';
import { DropboxQuery } from '../../../../shared/dropbox/dropbox.query';
import { DatePipe } from '@angular/common';
import { SbKeyMetric } from '../../../../shared/components/sb-key-metrics/sb-key-metrics.component';
import { Router } from '@angular/router';
import { UserSettingsQuery } from 'src/app/features/user-settings/state/user-settings.query';
import { TranslateService } from '@ngx-translate/core';
import { PositionsQuery } from 'src/app/features/positions/state/positions.query';

@Injectable({ providedIn: 'root' })
export class PrivateAssetQuery extends QueryEntity<PrivateAssetState> {
    baseCurrency = null;
    baseCurrency$ = this._userSettingsQuery
        .selectGlobalSetting$('BASE_CURRENCY')
        .pipe(filterNilValue())
        .subscribe((baseCurrency) => {
            this.baseCurrency = baseCurrency;
        });

    activeAssetTypeAsSnakeCase$ = this.select('activeAssetType').pipe(
        filterNilValue(),
        map((s) => s.replace(' ', '_').toUpperCase())
    );

    activeAssetType$ = this.select('activeAssetType').pipe(
        filterNilValue(),
        map((s) => s.replace(' ', '_').toUpperCase())
    );

    assets$ = combineLatest([
        this.selectAll(),
        this._positionsQuery.selectActivePositions$
    ]).pipe(
        map(([assets, positions]) =>
            assets.filter((asset) =>
                positions.some((p) => p.instrument.instrumentId === asset.instrumentId)
            )
        )
    );

    assetAllocations$ = combineLatest([
        this.assets$,
        this._translateService.currentLoader.getTranslation('en')
    ]).pipe(
        map(([assets, translations]) => {
            let totalMarketValue = 0;
            const groupedAssets: HashMap<
                Partial<{
                    type: string;
                    acquisitionValuePortfolioCcy: number;
                    numberOfAssets: number;
                    marketValuePortfolioCcy: number;
                    allocation: number;
                    name: string;
                    '1y': number;
                }>
            > = {
                ART: { type: 'ART', numberOfAssets: 0 },
                JEWELRY: { type: 'JEWELRY', numberOfAssets: 0 },
                REAL_ESTATE: { type: 'REAL_ESTATE', numberOfAssets: 0 },
                VEHICLE: { type: 'VEHICLE', numberOfAssets: 0 },
                OTHER: { type: 'OTHER', numberOfAssets: 0 }
            };
            for (let i = 0; i < assets.length; i++) {
                const asset = assets[i];
                if (!asset.sold && !asset.deleted) {
                    const marketValues = asset?.portfolioCcy.marketValues;
                    const sortedMarketValues = [...marketValues]?.sort(
                        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    const latestValuation =
                        sortedMarketValues?.length > 0
                            ? sortedMarketValues[sortedMarketValues.length - 1]?.price
                            : asset?.portfolioCcy?.acquisitionValue;
                    totalMarketValue += latestValuation;
                    if (groupedAssets[asset.type].numberOfAssets === 0) {
                        groupedAssets[asset.type] = {
                            type: asset.type,
                            acquisitionValuePortfolioCcy:
                                asset.portfolioCcy.acquisitionValue,
                            numberOfAssets: 1,
                            marketValuePortfolioCcy: latestValuation,
                            allocation: 0,
                            name: asset.type
                        };
                    } else {
                        groupedAssets[asset.type].acquisitionValuePortfolioCcy +=
                            asset.portfolioCcy.acquisitionValue;
                        groupedAssets[asset.type].numberOfAssets += 1;
                        groupedAssets[asset.type].marketValuePortfolioCcy +=
                            latestValuation;
                    }
                }
            }
            return Object.values(groupedAssets).map((groupedAsset) => {
                return {
                    ...groupedAsset,
                    name: translations[groupedAsset.type] || groupedAsset.type,
                    ccy: this.baseCurrency,
                    allocation:
                        groupedAsset?.marketValuePortfolioCcy !== null
                            ? groupedAsset?.marketValuePortfolioCcy / totalMarketValue
                            : 0,
                    '1y':
                        groupedAsset?.marketValuePortfolioCcy !== null
                            ? (groupedAsset?.marketValuePortfolioCcy -
                                  groupedAsset?.acquisitionValuePortfolioCcy) /
                              groupedAsset?.acquisitionValuePortfolioCcy
                            : 0
                };
            });
        }),
        shareReplay(1)
    );

    get isLoading$() {
        return this.selectLoading().pipe(shareReplay(1));
    }

    kpi$ = this.assets$.pipe(
        map((assets: PrivateAsset[]) => {
            return this.getKpi(assets);
        })
    );

    constructor(
        private router: Router,
        protected store: PrivateAssetStore,
        private formatCurrency: FormatCurrencyPipe,
        private dropboxQuery: DropboxQuery,
        private _userSettingsQuery: UserSettingsQuery,
        private datePipe: DatePipe,
        private _translateService: TranslateService,
        private _positionsQuery: PositionsQuery
    ) {
        super(store);
    }

    /**
     * Checks if the asset with a given name already exists
     * (compares with lowercase and trimmed name)
     * @param name
     */
    exists(name: string) {
        return this.selectAll({
            filterBy: (entity) =>
                entity.name?.trim().toLowerCase() === name?.trim().toLowerCase()
        }).pipe(map((entities) => entities.length > 0));
    }

    /**
     * Filters item for the current category private asset, e.g, arts, real estate etc.
     */
    getAssetsForCurrentAssetType$ = this.activeAssetType$.pipe(
        flatMap((activeAssetType) =>
            this.assets$.pipe(
                map((entities) => {
                    return entities
                        .filter(
                            (asset) =>
                                asset.deleted == false && asset.type == activeAssetType
                        )
                        .map((asset) => {
                            const latestValuation = asset?.marketValues?.reduce(
                                (prev, current) =>
                                    prev?.date > current?.date ? prev : current,
                                { date: null, price: null }
                            );
                            return {
                                ...asset,
                                valuationDate: latestValuation?.date,
                                marketValue: latestValuation?.price
                            };
                        })
                        .sort((a, b) =>
                            a.sold == b.sold
                                ? a.name.toLowerCase().localeCompare(b.name.toLowerCase())
                                : a.sold
                                ? 1
                                : -1
                        );
                })
            )
        )
    );

    getKpiForCurrentAssetType$() {
        return this.getAssetsForCurrentAssetType$.pipe(
            map((activeAssets: PrivateAsset[]) => this.getKpi(activeAssets))
        );
    }

    getKpi(assets: PrivateAsset[]): Array<SbKeyMetric> {
        const result: Array<SbKeyMetric> = [];

        const aggregatedValues = assets.reduce(
            (acc: any, next: PrivateAsset) => {
                if (!next.sold && !next.deleted) {
                    const marketValues = next?.portfolioCcy?.marketValues;
                    if (marketValues && marketValues?.length > 0) {
                        acc['marketValue'] +=
                            next?.portfolioCcy?.marketValues[0].price || 0;
                    } else {
                        // Fallback to acquisition value if market value is not available
                        acc['marketValue'] += next?.portfolioCcy?.acquisitionValue || 0;
                    }
                    acc['acquisitionValue'] +=
                        next?.portfolioCcy?.acquisitionValue ||
                        next.acquisitionValue ||
                        0;
                    acc['numberOfAssets'] += 1;
                    acc['cumulativeUnrealizedPnl'] =
                        (acc['marketValue'] || 0) - (acc['acquisitionValue'] || 0);
                    if (acc['acquisitionValue'] > 0) {
                        acc['returnInception'] =
                            acc['cumulativeUnrealizedPnl'] /
                            (acc['acquisitionValue'] || 0);
                    }
                }
                return acc;
            },
            { ...kpiInitialState }
        );

        const sinceInceptionValue = aggregatedValues['returnInception']
            ? aggregatedValues['returnInception']
            : 0;

        result.push({
            title: 'Performance',
            value: aggregatedValues['marketValue'] - aggregatedValues['acquisitionValue'],
            change: sinceInceptionValue,
            prefixPositiveValue: true,
            ccy: this._userSettingsQuery.getGlobalSetting('BASE_CURRENCY'),
            addColor: true,
            formatStyle: 'short'
        });

        result.push({
            title: 'Market Value',
            value: aggregatedValues['marketValue']
        });

        result.push({
            title: 'Acquisition Value',
            value: aggregatedValues['acquisitionValue']
        });

        result.push({
            title: 'Number of Assets',
            value: aggregatedValues['numberOfAssets']
        });

        if (this.router.routerState.snapshot.url !== '/private-assets/overview') {
            this.activeAssetType$
                .pipe(
                    filter((type) => !!type),
                    switchMap((type) =>
                        this.assetAllocations$.pipe(
                            map((allocations) => {
                                let index = -1;
                                index = allocations.findIndex(
                                    (AssetAllocation) => AssetAllocation.type === type
                                );
                                return index >= 0 ? allocations[index] : null;
                            })
                        )
                    ),
                    take(1)
                )
                .subscribe((allocation) => {
                    result.push({
                        title: 'Allocation',
                        value: allocation.allocation * 100,
                        suffix: '%',
                        maximumFractionDigits: 2
                    });
                });
        }

        return result;
    }

    getDetailsById$(id: number) {
        return this.selectEntity(id).pipe(
            filterNilValue(),
            map((entity) => {
                let details = [
                    {
                        key: 'Market value',
                        value$: this.formatCurrency.transform(
                            entity?.portfolioCcy.marketValues?.length > 0
                                ? entity?.portfolioCcy.marketValues[0]?.price
                                : 0,
                            entity?.portfolioCcy.currency
                        )
                    },
                    {
                        key: 'Acquisition value',
                        value$: this.formatCurrency.transform(
                            entity?.portfolioCcy.acquisitionValue,
                            entity?.portfolioCcy.currency
                        )
                    },
                    {
                        key: 'Acquisition date',
                        value$: this._userSettingsQuery
                            .selectGlobalSetting$('LOCALE')
                            .pipe(
                                map((locale) =>
                                    this.datePipe.transform(
                                        entity.acquisitionDate,
                                        'shortDate',
                                        null,
                                        locale
                                    )
                                )
                            )
                    },
                    {
                        key: 'Signed',
                        value: entity.meta.signed ? 'Yes' : 'No'
                    },
                    {
                        key: 'Vintage',
                        value: entity.meta.vintage || ''
                    },
                    {
                        key: 'Comment',
                        value: entity.comment || ''
                    }
                ];
                if (entity.sold) {
                    details = [
                        {
                            key: 'Date sold',
                            value$: this._userSettingsQuery
                                .selectGlobalSetting$('LOCALE')
                                .pipe(
                                    map((locale) =>
                                        this.datePipe.transform(
                                            entity.soldDate,
                                            'shortDate',
                                            null,
                                            locale
                                        )
                                    )
                                )
                        },
                        {
                            key: 'Value when sold',
                            value$: this.formatCurrency.transform(
                                entity?.portfolioCcy.soldPrice,
                                entity?.portfolioCcy.currency
                            )
                        },
                        ...details
                    ];
                }
                return details;
            })
        );
    }

    getOverviewDetailsById$(id: number) {
        return this.selectEntity(id).pipe(
            filterNilValue(),
            map((entity) => {
                const marketValue = {
                    key: 'Market value',
                    value$: this.formatCurrency.transform(
                        entity?.portfolioCcy.marketValues?.length > 0
                            ? entity?.portfolioCcy.marketValues[0]?.price
                            : 0,
                        entity?.portfolioCcy.currency,
                        true
                    )
                };
                const valuationDate = {
                    key: 'Valuation date',
                    value$: this._userSettingsQuery
                        .selectGlobalSetting$('LOCALE')
                        .pipe(
                            map((locale) =>
                                this.datePipe.transform(
                                    entity?.marketValues?.length > 0
                                        ? entity?.marketValues[0].date
                                        : null,
                                    'shortDate',
                                    null,
                                    locale
                                )
                            )
                        )
                };
                const comment = {
                    key: 'Comment',
                    value: entity.comment || ''
                };
                if (entity.type === 'REAL_ESTATE') {
                    return [
                        marketValue,
                        valuationDate,
                        {
                            key: 'Address',
                            value: entity.meta.address || ''
                        },
                        {
                            key: 'Property type',
                            value: entity.meta.propertyType || ''
                        },
                        {
                            key: 'Size',
                            value: entity.meta.size || ''
                        },
                        comment
                    ];
                } else if (entity.type === 'VEHICLE') {
                    return [
                        marketValue,
                        valuationDate,
                        {
                            key: 'Vintage',
                            value: entity.meta.vintage || ''
                        },
                        {
                            key: 'Mileage',
                            value: entity.meta.mileage || ''
                        },
                        {
                            key: 'Vehicle type',
                            value: entity.meta.vehicleType || ''
                        },
                        comment
                    ];
                } else if (entity.type === 'ART') {
                    return [
                        marketValue,
                        valuationDate,
                        {
                            key: 'Artist',
                            value: entity.meta.artist || ''
                        },
                        {
                            key: 'Vintage',
                            value: entity.meta.vintage || ''
                        },
                        {
                            key: 'Signed',
                            value: entity.meta.signed ? 'Yes' : 'No'
                        },
                        {
                            key: 'Art type',
                            value: entity.meta.artType || ''
                        },
                        {
                            key: 'Size',
                            value: entity.meta.size || ''
                        },
                        comment
                    ];
                } else if (entity.type === 'JEWELRY') {
                    return [
                        marketValue,
                        valuationDate,
                        {
                            key: 'Vintage',
                            value: entity.meta.vintage || ''
                        },
                        {
                            key: 'Box',
                            value: entity.meta.box ? 'Yes' : 'No'
                        },
                        {
                            key: 'Link',
                            value: entity.meta.link || ''
                        },
                        {
                            key: 'Brand',
                            value: entity.meta.brand || ''
                        },
                        {
                            key: 'Certificate',
                            value: entity.meta.certificate ? 'Yes' : 'No'
                        },
                        {
                            key: 'Jewelry type',
                            value: entity.meta.jewelryType || ''
                        },
                        comment
                    ];
                } else if (entity.type === 'OTHER') {
                    return [
                        marketValue,
                        valuationDate,
                        {
                            key: 'Vintage',
                            value: entity.meta.vintage || ''
                        },
                        {
                            key: 'Brand',
                            value: entity.meta.brand || ''
                        },
                        {
                            key: 'Box',
                            value: entity.meta.box ? 'Yes' : 'No'
                        },
                        {
                            key: 'Signed',
                            value: entity.meta.signed ? 'Yes' : 'No'
                        },
                        {
                            key: 'Certificate',
                            value: entity.meta.certificate ? 'Yes' : 'No'
                        },
                        {
                            key: 'Sub type',
                            value: entity.meta.subType || ''
                        },
                        comment
                    ];
                }
            })
        );
    }

    getTargetFolderForInstrumentId$(id) {
        const prefix = `private assets`;
        return combineLatest([
            this.selectEntity(id).pipe(filterNilValue()),
            this.dropboxQuery.basePath$
        ]).pipe(
            map(([privateAsset, basePath]) =>
                `${basePath}${prefix}/${privateAsset?.type.replace(/_/g, ' ')}/${
                    privateAsset?.name
                }`.toLowerCase()
            )
        );
    }
}
